import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Tabs,
  Tab,
  Snackbar,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Palette,
  Language,
  Check,
  ContentCopy,
} from '@mui/icons-material';
import {
  getBranding,
  updateBranding,
  updateDomain,
  BrandingSettings,
} from '../../services/api/partnerService';

const PartnerBrandingPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<BrandingSettings | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // ブランディングフォーム
  const [brandingForm, setBrandingForm] = useState({
    logo_url: '',
    favicon_url: '',
    primary_color: '#1976d2',
    secondary_color: '#333333',
    brand_name: '',
    support_email: '',
    support_url: '',
    terms_url: '',
    privacy_url: '',
  });

  // ドメインフォーム
  const [domainForm, setDomainForm] = useState({
    custom_domain: '',
    subdomain: '',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBranding();
      setSettings(data);
      setBrandingForm({
        logo_url: data.branding.logo_url || '',
        favicon_url: data.branding.favicon_url || '',
        primary_color: data.branding.primary_color || '#1976d2',
        secondary_color: data.branding.secondary_color || '#333333',
        brand_name: data.branding.brand_name || '',
        support_email: data.branding.support_email || '',
        support_url: data.branding.support_url || '',
        terms_url: data.branding.terms_url || '',
        privacy_url: data.branding.privacy_url || '',
      });
      setDomainForm({
        custom_domain: data.custom_domain || '',
        subdomain: data.subdomain || '',
      });
    } catch (err: any) {
      console.error('Settings load error:', err);
      setError(err.response?.data?.detail || '設定の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await updateBranding(brandingForm);
      setSnackbar('ブランディング設定を保存しました');
      await loadSettings();
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDomain = async () => {
    setSaving(true);
    try {
      await updateDomain(
        domainForm.custom_domain || undefined,
        domainForm.subdomain || undefined
      );
      setSnackbar('ドメイン設定を保存しました');
      await loadSettings();
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar('コピーしました');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          ブランディング・ドメイン設定
        </Typography>
        <Typography variant="body2" color="text.secondary">
          決済ページのブランディングとドメインを設定
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<Palette />} label="ブランディング" />
        <Tab icon={<Language />} label="ドメイン" />
      </Tabs>

      {/* ブランディング設定 */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ブランド情報
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="ブランド名"
                      value={brandingForm.brand_name}
                      onChange={(e) => setBrandingForm({ ...brandingForm, brand_name: e.target.value })}
                      placeholder="例: ACME Solutions"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ロゴURL"
                      value={brandingForm.logo_url}
                      onChange={(e) => setBrandingForm({ ...brandingForm, logo_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ファビコンURL"
                      value={brandingForm.favicon_url}
                      onChange={(e) => setBrandingForm({ ...brandingForm, favicon_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                  カラー
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="プライマリカラー"
                      type="color"
                      value={brandingForm.primary_color}
                      onChange={(e) => setBrandingForm({ ...brandingForm, primary_color: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: brandingForm.primary_color,
                                borderRadius: 1,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="セカンダリカラー"
                      type="color"
                      value={brandingForm.secondary_color}
                      onChange={(e) => setBrandingForm({ ...brandingForm, secondary_color: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: brandingForm.secondary_color,
                                borderRadius: 1,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                  サポート情報
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="サポートメール"
                      value={brandingForm.support_email}
                      onChange={(e) => setBrandingForm({ ...brandingForm, support_email: e.target.value })}
                      placeholder="support@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="サポートURL"
                      value={brandingForm.support_url}
                      onChange={(e) => setBrandingForm({ ...brandingForm, support_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="利用規約URL"
                      value={brandingForm.terms_url}
                      onChange={(e) => setBrandingForm({ ...brandingForm, terms_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="プライバシーポリシーURL"
                      value={brandingForm.privacy_url}
                      onChange={(e) => setBrandingForm({ ...brandingForm, privacy_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveBranding}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <Check />}
                  >
                    保存
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  プレビュー
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  {brandingForm.logo_url ? (
                    <img
                      src={brandingForm.logo_url}
                      alt="Logo"
                      style={{ maxWidth: '100%', maxHeight: 60 }}
                    />
                  ) : (
                    <Typography
                      variant="h5"
                      sx={{ color: brandingForm.primary_color }}
                    >
                      {brandingForm.brand_name || 'ブランド名'}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: brandingForm.primary_color,
                        '&:hover': { bgcolor: brandingForm.primary_color },
                      }}
                    >
                      購入する
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ドメイン設定 */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  カスタムドメイン
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  独自ドメインを設定して、決済ページを自社ブランドで表示できます。
                </Typography>

                <TextField
                  fullWidth
                  label="カスタムドメイン"
                  value={domainForm.custom_domain}
                  onChange={(e) => setDomainForm({ ...domainForm, custom_domain: e.target.value })}
                  placeholder="pay.example.com"
                  sx={{ mb: 3 }}
                />

                {domainForm.custom_domain && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      DNS設定手順
                    </Typography>
                    <Typography variant="body2">
                      以下のCNAMEレコードをDNSに追加してください:
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{domainForm.custom_domain} CNAME pay.gambit.icu</span>
                      <Button
                        size="small"
                        onClick={() => handleCopy(`${domainForm.custom_domain} CNAME pay.gambit.icu`)}
                      >
                        <ContentCopy fontSize="small" />
                      </Button>
                    </Box>
                  </Alert>
                )}

                {settings?.domain_verified && (
                  <Chip
                    label="検証済み"
                    color="success"
                    icon={<Check />}
                    sx={{ mb: 2 }}
                  />
                )}

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                  サブドメイン（無料）
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  gambit.icuのサブドメインを無料で利用できます。
                </Typography>

                <TextField
                  fullWidth
                  label="サブドメイン"
                  value={domainForm.subdomain}
                  onChange={(e) => setDomainForm({ ...domainForm, subdomain: e.target.value })}
                  placeholder="your-company"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        .pay.gambit.icu
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <Box sx={{ mt: 4 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveDomain}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <Check />}
                  >
                    保存
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  決済ページURL
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  現在有効なURL:
                </Typography>
                {domainForm.custom_domain && settings?.domain_verified ? (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'success.light',
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="success.dark" fontWeight="bold">
                      https://{domainForm.custom_domain}/
                    </Typography>
                  </Box>
                ) : domainForm.subdomain ? (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'primary.light',
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="primary.dark" fontWeight="bold">
                      https://{domainForm.subdomain}.pay.gambit.icu/
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      https://pay.gambit.icu/
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* スナックバー */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
};

export default PartnerBrandingPage;
